import { useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BuyerActivityStatusSubscription,
  BuyerActivityStatusSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { BuyerActivityObjectType } from "lib/types/graphQLEnums";
import { useSubscription } from "../useGraphQlClient";
import { updateBuyerActivityCache } from "./updateBuyerActivityCache";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _buyerActivityStatus = graphql(`
  subscription buyerActivityStatus($request: BuyerActivityStatusRequest!) {
    buyerActivityStatus(BuyerActivityStatusRequest: $request) {
      isComplete
      result {
        status
        questions {
          question
          answer
          sources {
            type
            id
            url
            title
            chunkNumber
            documentType
            filePath
            isDeleted
            citationId
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
        relatedNotices {
          guid
          name
          stagev2
          expiryDate
          closeDate
          publishDate
          value
          lots {
            suppliers {
              guid
              name
            }
          }
          procurementStage {
            id
            stage
          }
        }
      }
    }
  }
`);

type BuyerActivityStatusProps = {
  noticeIds: string[];
  buyerId: string;
  objectId: string;
  objectType: BuyerActivityObjectType;
  enabled?: boolean;
};

export function useBuyerActivityStatus({
  buyerId,
  noticeIds,
  objectId,
  objectType,
  enabled = true,
}: BuyerActivityStatusProps) {
  const queryClient = useQueryClient();

  return useSubscription<BuyerActivityStatusSubscription, BuyerActivityStatusSubscriptionVariables>(
    `subscription buyerActivityStatus($request: BuyerActivityStatusRequest!) {
        buyerActivityStatus(BuyerActivityStatusRequest: $request) {
            isComplete
            result {
              status
              questions {
                question
                answer
                sources {
                  type
                  id
                  url
                  title
                  chunkNumber
                  documentType
                  filePath
                  isDeleted
                  citationId
                  locations {
                    pageNumber
                    bbox {
                      x
                      y
                      width
                      height
                    }
                  }
                }
              }
              relatedNotices {
                guid
                name
                stagev2
                expiryDate
                closeDate
                publishDate
                value
                lots {
                  suppliers {
                    guid
                    name
                  }
                }
                procurementStage {
                  id
                  stage
                }
              }
            }
          }
        }
    `,
    {
      request: {
        buyerId,
        noticeIds,
      },
    },
    {
      onData: async (data) => {
        if (data.buyerActivityStatus.isComplete) {
          await updateBuyerActivityCache(
            queryClient,
            objectId,
            objectType,
            data.buyerActivityStatus.result,
          );
        }
      },
      enabled,
    },
  );
}
